import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';

import { environment } from '../../../environments/environment';

export type SupportConversationStatus = 'open' | 'closed';

export interface SupportAttachmentMeta {
  attachment_url: string;
  attachment_name: string;
  attachment_mime: string;
  attachment_size: number;
}

export interface SupportChatMessage {
  sender: string;
  body: string;
  created_on: string;
  attachment_url?: string | null;
  attachment_name?: string | null;
  attachment_mime?: string | null;
  attachment_size?: number | null;
}

export interface SupportConversationSummary {
  conversation_id: string;
  status: SupportConversationStatus;
  last_message_at: string | null;
  created_on: string;
  closed_on: string | null;
  preview: string | null;
}

export interface SupportChatThread {
  conversation_id: string | null;
  status: SupportConversationStatus | null;
  messages: SupportChatMessage[];
}

export interface SupportCloseResult {
  conversation_id: string;
  deleted: boolean;
  status: SupportConversationStatus;
}

interface SupportThreadResponse {
  data?: {
    conversation_id?: string | null;
    status?: string | null;
    messages?: SupportChatMessage[];
  };
}

interface SupportPostResponse {
  data?: {
    conversation_id?: string;
    message?: SupportChatMessage;
  };
}

interface SupportAttachmentResponse {
  data?: {
    conversation_id?: string;
    attachment?: SupportAttachmentMeta;
  };
}

function normalizeMessage(
  message: SupportChatMessage | undefined | null,
): SupportChatMessage | null {
  if (
    !message ||
    typeof message.sender !== 'string' ||
    typeof message.body !== 'string'
  ) {
    return null;
  }
  return {
    sender: message.sender,
    body: message.body,
    created_on: String(message.created_on ?? ''),
    attachment_url: message.attachment_url ?? null,
    attachment_name: message.attachment_name ?? null,
    attachment_mime: message.attachment_mime ?? null,
    attachment_size:
      message.attachment_size == null ? null : Number(message.attachment_size),
  };
}

interface SupportConversationsResponse {
  data?: {
    conversations?: SupportConversationSummary[];
  };
}

interface SupportConversationResponse {
  data?: {
    conversation?: SupportConversationSummary & {
      deleted?: boolean;
    };
  };
}

function normalizeStatus(
  value: string | null | undefined,
): SupportConversationStatus | null {
  if (value === 'open' || value === 'closed') return value;
  return null;
}

function normalizeConversation(
  row: SupportConversationSummary | undefined | null,
): SupportConversationSummary | null {
  if (!row || typeof row.conversation_id !== 'string' || !row.conversation_id) {
    return null;
  }
  const status = normalizeStatus(row.status) ?? 'open';
  return {
    conversation_id: row.conversation_id,
    status,
    last_message_at: row.last_message_at ?? null,
    created_on: row.created_on ?? '',
    closed_on: row.closed_on ?? null,
    preview: row.preview ?? null,
  };
}

@Injectable({
  providedIn: 'root',
})
export class SupportMessagesService {
  constructor(private http: HttpClient) {}

  listConversations(): Observable<SupportConversationSummary[]> {
    return this.http
      .get<SupportConversationsResponse>(
        `${environment.baseUrl}/support/conversations`,
      )
      .pipe(
        map((response) => {
          const rows = response?.data?.conversations ?? [];
          if (!Array.isArray(rows)) return [];
          return rows
            .map((row) => normalizeConversation(row))
            .filter((row): row is SupportConversationSummary => row !== null);
        }),
      );
  }

  startConversation(): Observable<SupportConversationSummary> {
    return this.http
      .post<SupportConversationResponse>(
        `${environment.baseUrl}/support/conversations`,
        {},
      )
      .pipe(
        map((response) => {
          const conversation = normalizeConversation(
            response?.data?.conversation ?? null,
          );
          if (!conversation) {
            throw new Error('Missing support conversation');
          }
          return conversation;
        }),
      );
  }

  closeConversation(conversationId: string): Observable<SupportCloseResult> {
    return this.http
      .post<SupportConversationResponse>(
        `${environment.baseUrl}/support/conversations/${conversationId}/close`,
        {},
      )
      .pipe(
        map((response) => {
          const conversation = response?.data?.conversation;
          if (
            !conversation ||
            typeof conversation.conversation_id !== 'string'
          ) {
            throw new Error('Missing support conversation');
          }
          return {
            conversation_id: conversation.conversation_id,
            deleted: Boolean(conversation.deleted),
            status: normalizeStatus(conversation.status) ?? 'closed',
          };
        }),
      );
  }

  getConversationMessages(
    conversationId: string,
  ): Observable<SupportChatThread> {
    return this.http
      .get<SupportThreadResponse>(
        `${environment.baseUrl}/support/conversations/${conversationId}/messages`,
      )
      .pipe(
        map((response) => {
          const messages = response?.data?.messages ?? [];
          return {
            conversation_id:
              response?.data?.conversation_id ?? conversationId ?? null,
            status: normalizeStatus(response?.data?.status),
            messages: Array.isArray(messages)
              ? messages
                  .map((message) => normalizeMessage(message))
                  .filter((message): message is SupportChatMessage => message !== null)
              : [],
          };
        }),
      );
  }

  uploadConversationAttachment(
    conversationId: string,
    file: File,
  ): Observable<SupportAttachmentMeta> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http
      .post<SupportAttachmentResponse>(
        `${environment.baseUrl}/support/conversations/${conversationId}/attachments`,
        form,
      )
      .pipe(
        map((response) => {
          const attachment = response?.data?.attachment;
          if (
            !attachment ||
            typeof attachment.attachment_url !== 'string' ||
            typeof attachment.attachment_name !== 'string' ||
            typeof attachment.attachment_mime !== 'string' ||
            attachment.attachment_size == null
          ) {
            throw new Error('Missing support attachment');
          }
          return {
            attachment_url: attachment.attachment_url,
            attachment_name: attachment.attachment_name,
            attachment_mime: attachment.attachment_mime,
            attachment_size: Number(attachment.attachment_size),
          };
        }),
      );
  }

  sendConversationMessage(
    conversationId: string,
    body: string,
    attachment?: SupportAttachmentMeta | null,
  ): Observable<SupportChatMessage> {
    const payload = attachment
      ? {
          body,
          attachment_url: attachment.attachment_url,
          attachment_name: attachment.attachment_name,
          attachment_mime: attachment.attachment_mime,
          attachment_size: attachment.attachment_size,
        }
      : { body };
    return this.http
      .post<SupportPostResponse>(
        `${environment.baseUrl}/support/conversations/${conversationId}/messages`,
        payload,
      )
      .pipe(
        map((response) => {
          const message = normalizeMessage(response?.data?.message);
          if (!message) {
            throw new Error('Missing support message');
          }
          return message;
        }),
      );
  }

  /** Legacy: current open chat. Prefer conversation routes for history/close. */
  getThread(): Observable<SupportChatThread> {
    return this.http
      .get<SupportThreadResponse>(`${environment.baseUrl}/support/messages`)
      .pipe(
        map((response) => {
          const messages = response?.data?.messages ?? [];
          return {
            conversation_id: response?.data?.conversation_id ?? null,
            status: normalizeStatus(response?.data?.status) ??
              (response?.data?.conversation_id ? 'open' : null),
            messages: Array.isArray(messages) ? messages : [],
          };
        }),
      );
  }

  /** Legacy: post to the current open chat. */
  sendMessage(
    body: string,
    attachment?: SupportAttachmentMeta | null,
  ): Observable<SupportChatMessage> {
    const payload = attachment
      ? {
          body,
          attachment_url: attachment.attachment_url,
          attachment_name: attachment.attachment_name,
          attachment_mime: attachment.attachment_mime,
          attachment_size: attachment.attachment_size,
        }
      : { body };
    return this.http
      .post<SupportPostResponse>(`${environment.baseUrl}/support/messages`, payload)
      .pipe(
        map((response) => {
          const message = normalizeMessage(response?.data?.message);
          if (!message) {
            throw new Error('Missing support message');
          }
          return message;
        }),
      );
  }
}
